import React from "react";
import {useMessages} from "i18n";
import {Mode} from "../gating";
import {Card, Check, Desc, IconTile, Method, ModeRadioInput, Name, Top} from "./ModeToggle.styles";

/**
 * The "How to send" selector (design `.method` radiogroup): two radio-cards — **Instant** (P2P, any
 * size, tab must stay open) and **Upload** (we host it, link outlives the tab). Each card carries an
 * icon tile, the mode name, a description line and a `title` tooltip. The active card gets a blue
 * border + accent tint, a filled blue icon tile, and a check.
 *
 * Restored in ui#215 after ui#214 had replaced it with label-only {@link ../../components/SegmentedTabs}
 * (which dropped the icons, descriptions and tooltips). It keeps ui#214's relocated position — its own
 * row after "Your email", above the Options button — and fills that row's width (see `Method`/`Card`).
 *
 * A dumb, token-driven view (epic #26): the selected {@link Mode} and its setter come from
 * {@link useCreateForm}; the copy comes from the i18n catalogue.
 */

interface Props {
	value: Mode;
	onChange: (mode: Mode) => void;
}

/** Bolt glyph — `currentColor` so it inverts on the active blue tile. */
function BoltGlyph() {
	return (
		<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
			<path d="M13 2 4 14h6l-1 8 9-12h-6z"/>
		</svg>
	);
}

/** Cloud-with-up-arrow glyph (the design's upload mode icon — richer than the plain `CloudIcon`). */
function CloudUpGlyph() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97A6 6 0 0 0 6.34 9.5 4 4 0 0 0 7 17.5"/>
			<path d="M12 12v6"/>
			<path d="m9 15 3-3 3 3"/>
		</svg>
	);
}

function CheckGlyph() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
			 strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M20 6 9 17l-5-5"/>
		</svg>
	);
}

export function ModeToggle({value, onChange}: Props) {
	const {mode: copy} = useMessages().sender;
	const cards: {mode: Mode; icon: React.ReactNode; name: string; desc: string; title: string}[] = [
		{mode: "instant", icon: <BoltGlyph/>, ...copy.instant},
		{mode: "upload", icon: <CloudUpGlyph/>, ...copy.upload},
	];

	return (
		<Method role="radiogroup" aria-label={copy.ariaLabel}>
			{cards.map(card => {
				const on = card.mode === value;
				return (
					<Card
						key={card.mode}
						$on={on}
						title={card.title}
					>
						<ModeRadioInput
							type="radio"
							name="transfer-mode"
							value={card.mode}
							checked={on}
							onChange={() => onChange(card.mode)}
						/>
						<Top>
							<IconTile $on={on} aria-hidden="true">{card.icon}</IconTile>
							<Name>{card.name}</Name>
							<Check $on={on} aria-hidden="true"><CheckGlyph/></Check>
						</Top>
						<Desc>{card.desc}</Desc>
					</Card>
				);
			})}
		</Method>
	);
}
